import React, { useRef, useState, useEffect, useMemo } from "react"
import _ from "lodash"

import { useNavigate } from "react-router-dom"
import { DateTime } from "luxon"
import { db } from "../../firebase"

// helpers
import trimURL from "../../helpers/trimURL"

// hooks
import useSubscribeAllDocs from "../../hooks/useSubscribeAllDocs"

// PR
import { Button } from "primereact/button"
import { SplitButton } from "primereact/splitbutton"
import { DataTable } from "primereact/datatable"
import { Column } from "primereact/column"
import { Toast } from "primereact/toast"

import Breadcrumb from "../../components/Breadcrumb"
import logger from "../../services/logger"

export default function HospitalDirectoryList() {
  const [pages, setPages] = useState([])
  const [currentPageIndex, setCurrentPageIndex] = useState(0)

  const hookParams = {
    collection: "hospital_directory",
    limit: 15,
    startAfterDoc: pages[currentPageIndex - 1] || null,
  }

  const [loading, error, data, lastVisible] = useSubscribeAllDocs(hookParams)

  useEffect(() => {
    if (lastVisible && pages[currentPageIndex] !== lastVisible) {
      const newPages = [...pages]
      newPages[currentPageIndex] = lastVisible
      setPages(newPages)
    }
  }, [lastVisible])

  const navigate = useNavigate()
  const toast = useRef(null)

  const handleDelete = async (docId) => {
    try {
      await db
        .collection("hospital_directory")
        .doc(docId)
        .delete()
      toast.current.show({
        severity: "success",
        summary: "Document Deleted",
        detail: "The document was successfully deleted.",
        life: 3000,
      })
    } catch (error) {
      toast.current.show({
        severity: "error",
        summary: "Error Deleting Document",
        detail: "An error occurred while deleting the document.",
        life: 3000,
      })
    }
  }

  const handleActive = async (docId) => {
    try {
      await db
        .collection("hospital_directory")
        .doc(docId)
        .update({
          facility_status: "ACTIVE",
        })
      toast.current.show({
        severity: "success",
        summary: "Hospital Activated",
        detail: "The Hospital was successfully activated.",
        life: 3000,
      })
    } catch (error) {
      logger.error("Error archiving document", error)
    }
  }

  const handleInactive = async (docId) => {
    try {
      await db
        .collection("hospital_directory")
        .doc(docId)
        .update({
          facility_status: "INACTIVE",
        })
      toast.current.show({
        severity: "success",
        summary: "Hospital Inactive",
        detail: "The Hospital was successfully inactived.",
        life: 3000,
      })
    } catch (error) {
      logger.error("Error archiving document", error)
    }
  }

  const handleData = useMemo(() => {
    if (!data) return []
    const orderedData = _.orderBy(data, ["facility_name"])

    return _.map(orderedData, (row) => {
      const handleStatus = () => {
        const { facility_status: status } = row
        switch (status) {
          case "ACTIVE":
            return (
              <div style={{ color: "var(--green-600)" }}>
                <i className="pi pi-circle-fill" style={{ marginRight: 10 }} />
                Active
              </div>
            )
          case "INACTIVE":
            return (
              <div style={{ color: "var(--red-400)" }}>
                <i className="pi pi-circle" style={{ marginRight: 10 }} />
                Inactive
              </div>
            )
        }
      }

      const handleActions = (row) => {
        const { id: docId, facility_status: status } = row
        const handleItems = () => {
          switch (status) {
            case "ACTIVE":
              return [
                {
                  label: "Inactivate",
                  icon: "pi pi-briefcase",
                  command: () => handleInactive(docId),
                },
              ]
            case "INACTIVE":
              return [
                {
                  label: "Activate",
                  icon: "pi pi-check",
                  command: () => handleActive(docId),
                },
              ]
          }
        }
        const items = handleItems()
        items.push({
          label: "Delete",
          icon: "pi pi-times",
          command: () => handleDelete(docId),
        })
        return (
          <SplitButton
            label="Edit"
            onClick={() => navigate(`/hospital-directory/edit/${docId}`)}
            model={items}
          />
        )
      }

      const modDate = row.timestamp_updated
        ? row.timestamp_updated.toDate()
        : null

      return {
        ...row,
        mrf_source_location: !_.isEmpty(row.mrf_source_location) ? (
          <a href={row.mrf_source_location} target="_black">
            <i className="pi pi-external-link" style={{ marginRight: 5 }} />
            {trimURL(row.mrf_source_location)}...
          </a>
        ) : null,
        facility_status: handleStatus(),
        updated: DateTime.fromJSDate(modDate).toFormat("F"),
        actions: handleActions(row),
      }
    })
  }, [data])

  if (error) {
    return (
      <div>
        <h2>Error when fetching.</h2>
      </div>
    )
  }

  return (
    <div className="container">
      <h1>Hospital Directory</h1>
      <Breadcrumb items={[{ label: "Hospital Directory" }]} />
      <Button
        onClick={() => navigate("/hospital-directory/add")}
        style={{ marginBottom: 15 }}
      >
        Add Hospital
      </Button>
      <DataTable value={handleData} responsiveLayout="scroll" loading={loading}>
        <Column field="facility_name" header="Facility Name" filter sortable />
        <Column
          field="facility_system"
          header="Facility System"
          filter
          sortable
        />
        <Column field="cms_number" header="CMS Number" filter sortable />
        <Column field="facility_state" header="State" filter sortable />
        <Column
          field="facility_coverage_area_cbsa"
          header="CBSA Coverage Area"
          filter
          sortable
        />
        <Column field="mrf_source_location" header="TIC URL" filter sortable />
        <Column
          field="facility_status"
          header="Hospital Status"
          style={{ width: 200 }}
        />
        <Column field="updated" header="Last Updated" />
        <Column field="actions" header="Actions" />
      </DataTable>

      <div
        style={{
          display: "flex",
          justifyContent: "center",
          gap: "1rem",
          marginTop: 16,
        }}
      >
        <Button
          label="Previous"
          icon="pi pi-angle-left"
          onClick={() => setCurrentPageIndex((i) => Math.max(i - 1, 0))}
          disabled={currentPageIndex === 0}
        />
        <Button
          label="Next"
          icon="pi pi-angle-right"
          iconPos="right"
          onClick={() => setCurrentPageIndex((i) => i + 1)}
          disabled={!lastVisible || !data || data.length < hookParams.limit}
        />
      </div>

      <Toast ref={toast} />
    </div>
  )
}
